import type { ViewFieldsFragment } from '@salescore/client-api'
import { useCanForView } from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  pruneSheetConfigTree,
  viewConfigKpiWithMetricToInline,
  type ViewQueryList,
} from '@salescore/core'
import { stableStringify } from '@salescore/features'
import { message } from 'antd'
import { t } from 'i18next'
import { useEffect, useRef } from 'react'
import { useRecoilValue } from 'recoil'

import { configAtom } from '../viewConfig'
import { viewsContextAtom } from './atoms'

// eslint-disable-next-line sonarjs/todo-tag
// TODO: 全てをselectorで書き直し
export const useSaveCurrentView = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
  query: ViewQueryList | undefined,
) => {
  const { updateView } = useRecoilValue(viewsContextAtom)

  const config = useRecoilValue(configAtom)
  const configReference = useRef(config)

  const canUpdate = useCanForView('update', view)

  useEffect(() => {
    configReference.current = config
  }, [config])

  const saveView = async (
    saveConfig: typeof config,
    userIntended: boolean,
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ): Promise<ViewFieldsFragment | undefined> => {
    const originalConfig = view.config
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
    if (originalConfig.type !== undefined && originalConfig.type !== saveConfig.type) {
      // XXX: originalConfigがなぜか{}になることがある？このケースへの対応で、originalConfig.typeのundefinedを許す
      // ありえないはずだが一応チェック
      return
    }

    // sheetでなければ、単純に保存するだけ
    if (saveConfig.type !== 'sheet') {
      if (!canUpdate) {
        // eslint-disable-next-line sonarjs/todo-tag
        void message.warning(t(`このビューを変更する権限がないため、変更は保存されません。`)) // TODO: セッション中に一度だけ表示したい。
        return
      }
      // 読み取り面 (ViewConfigKpiWithMetric) は inline を持たず formula.metric が編集対象になる。
      // 永続化は inline を入力形とし、読み取り時に attachMetricToView が inline から formula.metric を
      // 再導出するため、metric を inline に書き戻さず保存すると古い inline から metric が再導出され
      // 編集内容が失われる。kpi のみ保存境界で inline へ射影する。
      const configToSave = saveConfig.type === 'kpi' ? viewConfigKpiWithMetricToInline(saveConfig) : saveConfig
      try {
        const result = await updateView({
          id: view.id,
          config: configToSave,
          userIntended,
        })
        return result
      } catch (error) {
        if (error instanceof Error) {
          void message.error(error.message)
        } else {
          void message.error(t(`エラーが発生しました`))
        }
        return
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
    if (query === undefined || compiledConfig?.ui === undefined) {
      return
    }

    if (!canUpdate) {
      // eslint-disable-next-line sonarjs/todo-tag
      void message.warning(t(`このビューを変更する権限がないため、変更は保存されません。`)) // TODO: セッション中に一度だけ表示したい。
      return
    }

    const prunedTree = pruneSheetConfigTree(saveConfig)
    const prunedConfig = {
      ...saveConfig,
      tree: prunedTree,
    }

    try {
      const result = await updateView({
        id: view.id,
        config: prunedConfig,
        userIntended,
      })
      return result
    } catch (error) {
      if (error instanceof Error) {
        void message.error(error.message)
      } else {
        void message.error(t(`エラーが発生しました`))
      }
    }
  }

  return async (
    userIntended: boolean,
  ): Promise<{ view: ViewFieldsFragment | undefined; isConfigChangedDuringSave: boolean }> => {
    // React のバッチ更新では呼び出し時点の config が古い場合があるため、ref から保存対象を確定する。
    const saveConfig = configReference.current
    const savedView = await saveView(saveConfig, userIntended)
    return {
      view: savedView,
      isConfigChangedDuringSave: stableStringify(configReference.current) !== stableStringify(saveConfig),
    }
  }
}
